import { KSK2070_R01Dto, SubtotalData, DateSubtotalData, ItemSubtotalRow, DateSubtotalRow, GridRowData, SUBTOTAL_LEVELS, SUBTOTAL_TYPES } from '../Dto_KSK2070_R01';
import { ExtendedKSK2070_R01Dto } from '../Store_KSK2070_R01';
import { mergeDataWithSubtotalsGeneric } from 'src/utils/subtotal/merge';
import { createSubtotalGuards } from 'src/utils/subtotal/guards';
import { formatDate, toLocalDateTime } from 'src/utils/string/stringUtil';

const { isSubtotalRow } = createSubtotalGuards<GridRowData>({
    isSubtotalKey: 'isSubtotal',
    typeKey: 'subtotalType',
    itemTypeValue: SUBTOTAL_TYPES.ITEM,
    dateTypeValue: SUBTOTAL_TYPES.DATE,
});

/**
 * 안전한 Date 변환 헬퍼
 * - Date, string, number 등 다양한 입력을 Date로 정규화
 * - 유효하지 않으면 undefined 반환
 */
const ensureDate = (value: any): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return isNaN(value.getTime()) ? undefined : value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
};

/**
 * 품목별 소계 데이터 계산
 */
export const calculateSubtotals = (data: ExtendedKSK2070_R01Dto[]): SubtotalData[] => {
    if (!data || data.length === 0) return [];
    const grouped = data.reduce((acc, item) => {
        const key = item.itemCd;
        if (!acc[key]) {
            acc[key] = {
                itemCd: item.itemCd,
                itemSpec: item.itemSpec || '',
                itemUnit: item.itemUnit || '',
                count: 0,
                orderQty: 0,
                productQty: 0,
                badQty: 0,
                acceptQty: 0
            };
        }
        acc[key].count += 1;
        acc[key].orderQty += Number(item.orderQty) || 0;
        acc[key].productQty += Number(item.productQty) || 0;
        acc[key].badQty += Number(item.badQty) || 0;
        acc[key].acceptQty += Number(item.acceptQty) || 0;
        return acc;
    }, {} as Record<string, SubtotalData>);
    return Object.values(grouped).sort((a, b) => a.itemCd.localeCompare(b.itemCd));
};

/**
 * 생산일자별 소계 데이터 계산
 */
export const calculateDateSubtotals = (data: ExtendedKSK2070_R01Dto[]): DateSubtotalData[] => {
    if (!data || data.length === 0) return [];
    const grouped = data.reduce((acc, item) => {
        const dateObj = ensureDate(item.productDt);
        const dateKey = dateObj ? dateObj.toISOString() : '';
        const key = `${item.itemCd}_${dateKey}`;
        if (!acc[key]) {
            acc[key] = {
                itemCd: item.itemCd,
                itemSpec: item.itemSpec || '',
                itemUnit: item.itemUnit || '',
				productDt: dateObj ? formatDate(dateObj) : formatDate(new Date()),
                count: 0,
                dateCount: 0,
                orderQty: 0,
                productQty: 0,
                badQty: 0,
                acceptQty: 0
            } as DateSubtotalData;
        }
        acc[key].count += 1;
        acc[key].dateCount += 1;
        acc[key].orderQty += Number(item.orderQty) || 0;
        acc[key].productQty += Number(item.productQty) || 0;
        acc[key].badQty += Number(item.badQty) || 0;
        acc[key].acceptQty += Number(item.acceptQty) || 0;
        return acc;
    }, {} as Record<string, DateSubtotalData>);
    
    return Object.values(grouped).sort((a, b) => {
        if (a.itemCd === b.itemCd) {
            const aTime = ensureDate(a.productDt)?.getTime() || 0;
            const bTime = ensureDate(b.productDt)?.getTime() || 0;
            return aTime - bTime;
        }
        return a.itemCd.localeCompare(b.itemCd);
    });
};

/**
 * 품목별 소계 행 데이터 생성
 */
export const createSubtotalRow = (subtotal: SubtotalData): ItemSubtotalRow => ({
    valueIndex: 0,
    orderIndex: 0,
    orderIndexSeq: 0,
    productResultNo: `ITEM_SUBTOTAL_${subtotal.itemCd}`,
    compCd: '',
    compNm: '',
	productDt: formatDate(new Date()),
    orderType: '',
    orderTypeNm: '',
    workplaceCd: '',
    workplaceNm: '',
    jobTeam: '',
    jobTeamNm: '',
    itemCd: subtotal.itemCd,
    itemNm: '',
    itemStatus: '',
    itemSpec: subtotal.itemSpec,
    itemUnit: subtotal.itemUnit,
    orderQty: subtotal.orderQty,
    productEmp: '',
    productEmpNm: '',
    empNm: '',
    productQty: subtotal.productQty,
    badQty: subtotal.badQty,
    acceptQty: subtotal.acceptQty,
    brandCd: '',
    brandNm: '',
    logicCenter: '',
    warehouseCd: '',
    warehouseNm: '',
	basOrderType: '',
    basOrderNo: '',
    basOrderItemNo: '',
	workStartDt: toLocalDateTime(new Date()),
	workEndDt: undefined,
	inspectQty: 0,
	inspectYn: 'N',
	kpiYn: 'N',
    remark: '',

    inserted: false,
    isModified: false,
    startDate: undefined,
    endDate: undefined,
    insertUser: undefined,
    insertForm: undefined,
    insertDt: undefined,
    updateUser: undefined,
    updateForm: undefined,
    updateDt: undefined,
    codeEntity: null,
    productOrderNo: '',
    count: subtotal.count,
    isSubtotal: true,
    subtotalType: SUBTOTAL_TYPES.ITEM,
    level: SUBTOTAL_LEVELS.ITEM,
});

/**
 * 생산일자별 소계 행 데이터 생성
 */
export const createDateSubtotalRow = (subtotal: DateSubtotalData): DateSubtotalRow => ({
    valueIndex: 0,
    orderIndex: 0,
    orderIndexSeq: 0,
    productResultNo: `DATE_SUBTOTAL_${subtotal.itemCd}_${subtotal.productDt}`,
    compCd: '',
    compNm: '',
	productDt: subtotal.productDt || formatDate(new Date()),
    orderType: '',
    orderTypeNm: '',
    workplaceCd: '',
    workplaceNm: '',
    jobTeam: '',
    jobTeamNm: '',
    itemCd: subtotal.itemCd,
    itemNm: '',
    itemSpec: subtotal.itemSpec,
    itemUnit: subtotal.itemUnit,
    itemStatus: '',
    orderQty: subtotal.orderQty,
    productEmp: '',
    productEmpNm: '',
    empNm: '',
    productQty: subtotal.productQty,
    badQty: subtotal.badQty,
    acceptQty: subtotal.acceptQty,
    brandCd: '',
    brandNm: '',
    logicCenter: '',
    warehouseCd: '',
    warehouseNm: '',
	basOrderType: '',
    basOrderNo: '',
    basOrderItemNo: '',
	workStartDt: toLocalDateTime(new Date()),
	workEndDt: undefined,
	inspectQty: 0,
	inspectYn: 'N',
	kpiYn: 'N',
    remark: '',
	
    inserted: false,
    isModified: false,
    startDate: undefined,
    endDate: undefined,
    insertUser: undefined,
    insertForm: undefined,
    insertDt: undefined,
    updateUser: undefined,
    updateForm: undefined,
    updateDt: undefined,
    codeEntity: null,
    productOrderNo: '',
    count: subtotal.count,
    isSubtotal: true,
    subtotalType: SUBTOTAL_TYPES.DATE,
    level: SUBTOTAL_LEVELS.DATE,
    dateCount: subtotal.dateCount,
});

/** 상세 행 생성 */
export const createDetailRow = (item: ExtendedKSK2070_R01Dto): GridRowData => ({
    ...item,
    isSubtotal: false,
    level: SUBTOTAL_LEVELS.DETAIL,
});

/** 공통 머지 유틸을 사용한 병합 */
export const mergeDataWithSubtotals = (
    original: ExtendedKSK2070_R01Dto[],
    subtotals: SubtotalData[],
    dateSubtotals: DateSubtotalData[],
    showSubtotals: boolean
): GridRowData[] => {
    const itemSubtotalMap = new Map<string, SubtotalData>(subtotals.map(s => [s.itemCd, s]));
    const dateSubtotalMap = new Map<string, DateSubtotalData>(
        dateSubtotals.map(s => {
            const d = ensureDate(s.productDt);
            const key = `${s.itemCd}|${d ? d.toISOString() : ''}`;
            return [key, s];
        })
    );

    return mergeDataWithSubtotalsGeneric<ExtendedKSK2070_R01Dto, GridRowData, SubtotalData, DateSubtotalData>(
        original,
        {
            getItemKey: (d) => d.itemCd,
            getDateKey: (d) => {
                const dd = ensureDate(d.productDt);
                return dd ? dd.toISOString() : '';
            },
            itemSubtotalMap,
            dateSubtotalMap,
            createDetailRow,
            createItemSubtotalRow: createSubtotalRow,
            createDateSubtotalRow,
            showSubtotals,
            sortDates: (a, b) => a.localeCompare(b),
        }
    );
};

/**
 * 특정 품목의 모든 상세 행 ID 목록 반환 (소계/표시 유틸 계층에 위치)
 */
export const getDetailRowIdsByItem = (itemCd: string, gridData: GridRowData[]): string[] => {
    return gridData
        .filter(row => !isSubtotalRow(row) && row.itemCd === itemCd)
        .map(row => row.productResultNo);
};
